import { CommonModule, formatDate } from "@angular/common";
import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatMenuModule, MatMenuTrigger } from "@angular/material/menu";
import { MatDialog } from "@angular/material/dialog";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { CustomRangeModalComponent } from "src/app/shared/components/custom-range-modal/custom-range-modal.component";
import { resolveAngularLocale } from "src/app/shared/helpers/locale.helper";
import { TimeUtil } from "src/app/shared/services/time-util.service";

export interface DateRangeValue {
  startDate: string | null;
  endDate: string | null;
}

export interface DateRangeOption {
  displayOption: string;
  value: DateRangeValue | "";
}

@Component({
  selector: "app-date-range-selector",
  standalone: true,
  templateUrl: "./date-range-selector.component.html",
  styleUrls: ["./date-range-selector.component.scss"],
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatMenuModule,
    MatButtonModule,
    TranslateModule,
  ],
})
export class DateRangeSelectorComponent implements OnChanges {
  @Input() label = "";
  @Input() value: DateRangeValue = { startDate: null, endDate: null };
  @Input() options: DateRangeOption[] = [];
  @Output() valueChange = new EventEmitter<DateRangeValue>();
  @Input({ required: true }) translateKeyPrefix = "";

  @ViewChild("fieldWrapper")
  private fieldWrapper?: ElementRef<HTMLElement>;

  @ViewChild("menuTrigger")
  menuTrigger?: MatMenuTrigger;

  @ViewChild("rangeInput")
  private rangeInput?: ElementRef<HTMLInputElement>;

  displayText = "";
  isMenuOpen = false;
  isFieldFocused = false;

  constructor(
    private dialog: MatDialog,
    private timeUtil: TimeUtil,
    private translateService: TranslateService,
    private destroyRef: DestroyRef,
  ) {
    this.translateService.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.updateDisplayText());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["options"] && (!this.options || this.options.length === 0)) {
      this.options = this.getDefaultOptions();
    }

    if (!this.options || this.options.length === 0) {
      this.options = this.getDefaultOptions();
    }

    if (changes["value"]) {
      this.updateDisplayText();
    }
  }

  private getDefaultOptions(): DateRangeOption[] {
    return [
      {
        displayOption: "None",
        value: "",
      },
      {
        displayOption: "Last 15 Months",
        value: this.timeUtil.getLastMonthsDateRange(15),
      },
      {
        displayOption: "Last 12 Months",
        value: this.timeUtil.getLastMonthsDateRange(12),
      },
      {
        displayOption: "Last 9 Months",
        value: this.timeUtil.getLastMonthsDateRange(9),
      },
      {
        displayOption: "Last 6 Months",
        value: this.timeUtil.getLastMonthsDateRange(6),
      },
      {
        displayOption: "Last 3 Months",
        value: this.timeUtil.getLastMonthsDateRange(3),
      },
      {
        displayOption: "Last Month",
        value: this.timeUtil.getLastMonthsDateRange(1),
      },
    ];
  }

  selectOption(option: DateRangeOption): void {
    if (option.value === "") {
      this.emitValue({ startDate: null, endDate: null });
      return;
    }

    this.emitValue({
      startDate: option.value.startDate || null,
      endDate: option.value.endDate || null,
    });
  }

  isSelected(option: DateRangeOption): boolean {
    if (!this.value) {
      return option.value === "";
    }

    if (option.value === "") {
      return !this.value.startDate && !this.value.endDate;
    }

    return (
      this.value.startDate === option.value.startDate &&
      this.value.endDate === option.value.endDate
    );
  }

  isCustomRangeSelected(): boolean {
    if (!this.value?.startDate || !this.value?.endDate) {
      return false;
    }

    // Check if the current range matches any predefined option
    const matchesPredefinedOption = this.options.some(
      (option) => option.value !== "" && this.isSelected(option),
    );

    // If it doesn't match any predefined option, customRange is selected
    return !matchesPredefinedOption;
  }

  openMenu(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.focusField();
    this.isMenuOpen = true;
    this.menuTrigger?.openMenu();
  }

  onMenuOpened(): void {
    this.isMenuOpen = true;
    this.focusField();
    this.syncMenuDropdownPosition();
  }

  onMenuClosed(): void {
    this.isMenuOpen = false;
    queueMicrotask(() => this.focusField());
  }

  private focusField(): void {
    this.rangeInput?.nativeElement.focus({ preventScroll: true });
  }

  openCustomRange(): void {
    const dialogRef = this.dialog.open(CustomRangeModalComponent, {
      autoFocus: false,
      disableClose: true,
      data: {
        start: this.timeUtil.fromApiDate(this.value?.startDate),
        end: this.timeUtil.fromApiDate(this.value?.endDate),
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) {
        queueMicrotask(() => this.focusField());
        return;
      }

      const formatted = {
        startDate: this.formatDate(result.start),
        endDate: this.formatDate(result.end),
      };

      this.emitValue(formatted);
      queueMicrotask(() => this.focusField());
    });
  }

  syncMenuDropdownPosition(): void {
    const fieldWidth = this.fieldWrapper?.nativeElement.offsetWidth;
    const panelId = this.menuTrigger?.menu?.panelId;

    if (!fieldWidth || !panelId) {
      return;
    }

    queueMicrotask(() => {
      const panel = document.getElementById(panelId);
      const overlayPane = panel?.closest(".cdk-overlay-pane") as HTMLElement | null;

      if (!panel || !overlayPane) {
        return;
      }

      panel.style.minWidth = `${fieldWidth}px`;
      panel.style.width = `${fieldWidth}px`;
      panel.style.maxWidth = `${fieldWidth}px`;

      const fieldRect = this.fieldWrapper!.nativeElement.getBoundingClientRect();
      overlayPane.style.left = `${fieldRect.left}px`;
      overlayPane.style.top = `${fieldRect.bottom}px`;
      overlayPane.style.transform = "none";
      overlayPane.style.width = `${fieldWidth}px`;
    });
  }

  private formatDate(date: Date): string | null {
    return date ? this.timeUtil.toApiDate(date) || null : null;
  }

  private formatDisplayDate(value: string): string | null {
    const locale = resolveAngularLocale(
      this.translateService.currentLang ||
        this.translateService.defaultLang,
    );

    try {
      return formatDate(value, "mediumDate", locale, "UTC");
    } catch {
      return null;
    }
  }

  private emitValue(value: DateRangeValue): void {
    this.value = value;
    this.updateDisplayText();
    this.valueChange.emit(value);
  }

  private updateDisplayText(): void {
    if (this.value?.startDate && this.value?.endDate) {
      const start = this.formatDisplayDate(this.value.startDate);
      const end = this.formatDisplayDate(this.value.endDate);
      this.displayText = start && end ? `${start} - ${end}` : "";
    } else {
      this.displayText = "";
    }
  }

  private translateKey(key: string): string {
    if (!key) {
      return "";
    }
    return this.translateService.instant("case_report." + key);
  }
}
